import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  goodsReturnsToSupplier,
  type GRTSListItem,
} from '@/api/goodsReturns';
import { goodsReceipts } from '@/api/goodsReceipts';
import { contractors as contractorsApi } from '@/api/catalogs';
import Drawer from '@/components/Drawer';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DatePopover from '@/components/DatePopover';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().slice(0, 16);

export default function GoodsReturnsToSupplierPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [selOrg, setSelOrg] = useState('');
  const [selReceipt, setSelReceipt] = useState('');
  const [number, setNumber] = useState('');
  const [date, setDate] = useState(today());
  const [comment, setComment] = useState('');

  const list = useQuery({
    queryKey: ['grts-list'],
    queryFn: () => goodsReturnsToSupplier.list({ limit: 200 }),
  });
  // UX α: orgList більше не використовується — переключилися на
  // availableOrgs з usePermissionsByOrg (показуємо лише org з
  // purchases.return.manage permission).
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
  } = usePermissionsByOrg('purchases.return.manage');
  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 500 }),
  });
  const receiptsQ = useQuery({
    queryKey: ['goods-receipts-posted'],
    queryFn: () => goodsReceipts.list({ posted: true, limit: 200 }),
    enabled: createOpen,
  });

  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);

  const create = useMutation({
    mutationFn: () => goodsReturnsToSupplier.createFromReceipt({
      organization_id: selOrg,
      document_number: number,
      document_date: new Date(date).toISOString(),
      base_receipt_id: selReceipt,
      comment: comment || null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['grts-list'] });
      setCreateOpen(false);
      navigate(`/goods-returns-to-supplier/${doc.id}`);
    },
  });

  const cols = useMemo<ColumnDef<GRTSListItem>[]>(() => [
    { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'document_date', header: t('cd.date'), width: 170,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString(i18n.language) : '',
    },
    {
      key: 'contractor_id', header: 'Постачальник',
      sortValue: (r) => cName.get(r.contractor_id) ?? '',
      cell: (r) => cName.get(r.contractor_id) ?? '',
    },
    {
      key: 'total_amount', header: t('cd.amount'), width: 140, align: 'right',
      sortValue: (r) => Number(r.total_amount),
      cell: (r) => fmt(r.total_amount),
    },
    {
      key: 'posted', header: t('cd.posted'), width: 130, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0,
      cell: (r) => r.posted ? '✓ ' + t('mo.posted_yes') : t('mo.posted_no'),
    },
  ], [t, i18n.language, cName]);

  // UX α: defaultну org беремо з availableOrgs (де є purchases.return.manage)
  if (!selOrg && availableOrgs[0]) setSelOrg(availableOrgs[0].id);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Повернення постачальникам</h2>
        <button
          className="primary"
          onClick={() => setCreateOpen(true)}
          disabled={!hasPermissionInAnyOrg}
          title={!hasPermissionInAnyOrg
            ? `У вас немає прав створювати повернення у жодній з ${totalAccessibleOrgs} ваших організацій`
            : undefined
          }
        >
          + Створити з поступлення
        </button>
      </div>
      <DataTable<GRTSListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/goods-returns-to-supplier/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />

      <Drawer
        open={createOpen}
        title="Нове повернення постачальнику"
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <button onClick={() => setCreateOpen(false)}>{t('common.cancel')}</button>
            <button
              className="primary"
              disabled={!selReceipt || !number || create.isPending}
              onClick={() => create.mutate()}
            >
              {t('common.create')}
            </button>
          </>
        }
      >
        <div className="field">
          <label>Організація</label>
          <select value={selOrg} onChange={(e) => setSelOrg(e.target.value)}>
            {availableOrgs.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>Документ-підстава (поступлення)</label>
          <select value={selReceipt} onChange={(e) => setSelReceipt(e.target.value)}>
            <option value="">—</option>
            {receiptsQ.data?.items.map((r) => (
              <option key={r.id} value={r.id}>
                {r.document_number} ({new Date(r.document_date).toLocaleDateString('uk-UA')}) — {fmt(r.total_amount)}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <div className="field">
            <label>№ документа</label>
            <input value={number} onChange={(e) => setNumber(e.target.value)} required />
          </div>
          <div className="field">
            <label>Дата</label>
            <DatePopover withTime value={date} onChange={(v) => setDate(v)} />
          </div>
        </div>
        <div className="field">
          <label>Коментар</label>
          <input value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
      </Drawer>
    </>
  );
}
